import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';
import { message } from 'ant-design-vue';

/**
 * Extended Axios request config supporting a per-request `skipToast` flag.
 * When set to true, the response interceptor will NOT show a global toast
 * for error responses — the caller is responsible for surfacing the error
 * (e.g. via an inline <a-alert> bound to a store/composable error ref).
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    skipToast?: boolean;
  }
}

/**
 * API client instance with auth interceptors
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: Add JWT token and trace ID to headers
apiClient.interceptors.request.use(
  (config) => {
    // Add trace ID from window (set by useTraceId composable)
    const traceId = (window as any).__HYPERKE_TRACE_ID || crypto.randomUUID();
    config.headers['X-Request-ID'] = traceId;

    // Always check localStorage first (most reliable source of truth)
    const storedToken = localStorage.getItem('token');
    const authStore = useAuthStore();
    const storeToken = authStore.token;

    // Debug logging
    console.log('[API Client] Request interceptor:', {
      url: config.url,
      method: config.method,
      traceId,
      hasStoredToken: !!storedToken,
      hasStoreToken: !!storeToken,
      storedTokenLength: storedToken?.length || 0,
      storeTokenLength: storeToken?.length || 0,
      tokensMatch: storedToken === storeToken,
    });

    // Per-request explicit Authorization must win (impersonation end sends the stashed admin token)
    if (config.headers.Authorization) {
      console.log('[API Client] Using per-request Authorization header (explicit override):', {
        url: config.url,
        headerLength: String(config.headers.Authorization).length,
      });
    } else if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
      console.log('[API Client] Using token from localStorage, header set:', {
        headerLength: config.headers.Authorization?.length || 0,
        tokenPrefix: storedToken.substring(0, 20) + '...',
      });
      // Sync with store
      if (authStore.token !== storedToken) {
        console.log('[API Client] Syncing store with localStorage token');
        authStore.token = storedToken;
      }
    } else if (storeToken) {
      config.headers.Authorization = `Bearer ${storeToken}`;
      console.log('[API Client] Using token from store (localStorage empty), header set:', {
        headerLength: config.headers.Authorization?.length || 0,
        tokenPrefix: storeToken.substring(0, 20) + '...',
      });
    } else {
      console.warn('[API Client] No token available for request:', config.url);
    }

    return config;
  },
  (error) => {
    console.error('[API Client] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Human-readable countdown for Retry-After (seconds).
 * Examples: "30 seconds", "1 minute", "2 minutes".
 */
function formatRetryAfter(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * Specific, actionable error messages keyed by HTTP status code.
 * 401 and 422 are handled separately below (401 triggers auth flow,
 * 422 prefers the server-provided message).
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  403: "You don't have permission to perform this action. Contact your administrator if you believe this is an error.",
  404: 'The requested resource was not found. It may have been deleted or moved.',
  409: 'This action conflicts with existing data. Refresh the page and try again.',
  500: "Server error. We've been notified — please try again in a few minutes.",
  502: 'The service is temporarily unavailable. Please try again shortly.',
  503: 'The service is under maintenance. Please try again in a few minutes.',
  504: 'The request timed out. Please try again.',
};

/**
 * Status codes that benefit from a longer toast duration (6s).
 * These are errors where the user needs time to read the guidance.
 */
const LONG_DURATION_STATUSES = new Set<number>([401, 403, 404, 429, 500, 502, 503, 504]);

const DEFAULT_TOAST_DURATION = 3; // seconds
const LONG_TOAST_DURATION = 6; // seconds

// Guard against concurrent 401s triggering multiple logout/redirect cycles
let isLoggingOut = false;

// Response interceptor: Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Extract request ID from response headers for correlation
    const requestId = response.headers['x-request-id'] || 'unknown';
    if (import.meta.env.DEV) {
      console.log('[API Client] Response success:', {
        url: response.config.url,
        status: response.status,
        requestId,
      });
    }
    return response;
  },
  (error: AxiosError) => {
    // Fix: Silently ignore canceled requests (expected behavior when switching tabs/routes)
    // Axios uses 'canceled' message for aborted requests
    if (error.message === 'canceled' || error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
      // Don't log canceled requests - they're expected when aborting previous requests
      return Promise.reject(error);
    }

    const requestId = error.response?.headers['x-request-id'] || 'unknown';
    const status = error.response?.status;
    const data = error.response?.data as any;
    const url = error.config?.url;
    const skipToast = Boolean(
      error.config?.skipToast || error.config?.headers?.['X-Skip-Toast']
    );

    console.error('[API Client] Response error:', {
      url,
      method: error.config?.method,
      status,
      statusText: error.response?.statusText,
      message: error.message,
      requestId,
      // Don't log sensitive data
      responseData: error.response?.data,
    });

    // Helper to show a toast unless the caller opted out via skipToast.
    const showToast = (content: string, durationOverride?: number) => {
      if (skipToast) return;
      const duration =
        durationOverride ?? (status && LONG_DURATION_STATUSES.has(status)
          ? LONG_TOAST_DURATION
          : DEFAULT_TOAST_DURATION);
      message.error({ content, duration });
    };

    // Handle 401 Unauthorized - logout and redirect (skip for login requests)
    if (status === 401) {
      // Don't logout if this is a login request - let the login component handle the error.
      // Match robustly: login endpoint may have query strings, trailing slashes, or version prefixes.
      const isLoginRequest = typeof url === 'string' && /\/auth\/login(\?|\/|$)/.test(url);
      if (!isLoginRequest) {
        // Impersonation rescue: the 1h impersonation token can die while the
        // admin's 7d session is still valid — restore the admin instead of
        // running a full logout() that would delete the stashed original token.
        const stashedAdminToken = localStorage.getItem('original_admin_token');
        const stashedAdminUser = localStorage.getItem('original_admin_user');
        let currentlyImpersonating = false;
        try {
          currentlyImpersonating =
            JSON.parse(localStorage.getItem('user') || 'null')?.is_impersonation === true;
        } catch {
          currentlyImpersonating = false;
        }
        if (stashedAdminToken && stashedAdminUser && currentlyImpersonating) {
          if (!isLoggingOut) {
            isLoggingOut = true;
            console.warn('[API Client] 401 during impersonation - restoring original admin session');
            localStorage.setItem('token', stashedAdminToken);
            localStorage.setItem('user', stashedAdminUser);
            localStorage.removeItem('original_admin_token');
            localStorage.removeItem('original_admin_user');
            message.warning({
              content: 'Impersonation session expired — returning to your admin account.',
              duration: LONG_TOAST_DURATION,
            });
            setTimeout(() => {
              window.location.reload();
              isLoggingOut = false;
            }, 1500);
          }
          return Promise.reject(error);
        }

        console.error('[API Client] 401 Unauthorized - logging out:', {
          url,
          requestId,
          responseMessage: data?.message,
        });
        showToast('Your session has expired. Redirecting to login...', LONG_TOAST_DURATION);
        // Guard against concurrent 401s firing multiple logouts simultaneously
        if (!isLoggingOut) {
          isLoggingOut = true;
          const authStore = useAuthStore();
          // Slight delay so the user sees the toast before the redirect kicks in
          setTimeout(() => {
            authStore.logout(); // This already handles redirect via window.location.href
            isLoggingOut = false;
          }, 2000);
        }
      }
      // Don't call router.push here - logout() already handles redirect
      return Promise.reject(error);
    }

    // Handle 429 Too Many Requests - rate limited, parse Retry-After header
    if (status === 429) {
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const seconds = parseInt(retryAfterHeader as string, 10) || 60;
      const humanDuration = formatRetryAfter(seconds);
      showToast(
        `Too many requests. Please try again in ${humanDuration}.`,
        LONG_TOAST_DURATION
      );
      return Promise.reject(new Error(`Rate limited: retry in ${seconds}s`));
    }

    // Handle network errors (no response received)
    if (!error.response) {
      if (!navigator.onLine) {
        showToast(
          'You appear to be offline. Please check your internet connection and try again.',
          LONG_TOAST_DURATION
        );
      } else {
        showToast(
          'Could not reach the server. The service may be down — please try again in a moment.',
          LONG_TOAST_DURATION
        );
      }
      return Promise.reject(new Error('Network error'));
    }

    // 422 Validation Error - prefer server-provided message
    if (status === 422) {
      const serverMessage =
        data && typeof data === 'object' && 'message' in data
          ? String((data as any).message)
          : '';
      showToast(serverMessage || 'Some of the submitted data is invalid. Please review and try again.');
      return Promise.reject(error);
    }

    // Map remaining known status codes to specific messages
    if (status && ERROR_MESSAGES[status]) {
      showToast(ERROR_MESSAGES[status]);
      return Promise.reject(error);
    }

    // Generic 5xx fallback (covers uncommon codes like 501, 505, 507, ...)
    if (status && status >= 500) {
      showToast(ERROR_MESSAGES[500]);
      return Promise.reject(error);
    }

    // Fallback: use server-provided message if present, else generic
    if (data && typeof data === 'object' && 'message' in data) {
      showToast(String((data as any).message || 'Request failed. Please try again.'));
      return Promise.reject(error);
    }

    // Unknown error - fallback
    showToast('Request failed. Please try again.');
    return Promise.reject(error);
  }
);

export { apiClient };
export default apiClient;
